'use client';

import { FormActions, FormField } from '@/features/forms/FormPrimitives';
import Form from 'next/form.js';
import { Button, Modal } from 'react-bootstrap';
import { readMessage } from '../reviewUtils';

export default function ReviewModal({
  closeReviewModal,
  deleteActionLabel,
  formComment,
  formError,
  formRating,
  handleDeleteReview,
  handleSubmit,
  isModalOpen,
  isRefreshing,
  messages,
  ownReviewState,
  pendingAction,
  setFormComment,
  setFormRating,
}) {
  const ratingRequiredMessage = readMessage(
    messages,
    'writeReview.ratingRequired',
    'Select a rating before publishing your review.',
  );
  const commentRequiredMessage = readMessage(
    messages,
    'writeReview.commentRequired',
    'Add a comment before publishing your review.',
  );
  const ratingError = formError === ratingRequiredMessage ? formError : '';
  const commentError = formError === commentRequiredMessage ? formError : '';
  const generalError = ratingError || commentError ? '' : formError;

  return (
    <Modal
      show={isModalOpen}
      onHide={closeReviewModal}
      centered
      className="product-review-modal app-glass-modal"
      backdropClassName="app-glass-modal-backdrop"
      dialogClassName="product-review-modal__dialog"
      contentClassName="product-review-modal__content"
    >
      <Modal.Header closeButton className="product-review-modal__header">
        <Modal.Title className="product-review-modal__title">
          {ownReviewState.isReviewed
            ? readMessage(
                messages,
                'writeReview.title.edit',
                'Update Your Review',
              )
            : readMessage(
                messages,
                'writeReview.title.new',
                'Write a Customer Review',
              )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="product-review-modal__body">
        <Form className="product-review-form" action={handleSubmit}>
          <FormField
            as="select"
            label={readMessage(messages, 'writeReview.rating', 'Rating')}
            value={formRating}
            onChange={(event) => setFormRating(event.target.value)}
            required
            showRequiredAsterisk
            selectPlaceholder={readMessage(
              messages,
              'writeReview.selectRating',
              'Select...',
            )}
            options={[
              {
                value: '1',
                label: readMessage(
                  messages,
                  'writeReview.rating_1',
                  '1 - Poor',
                ),
              },
              {
                value: '2',
                label: readMessage(
                  messages,
                  'writeReview.rating_2',
                  '2 - Fair',
                ),
              },
              {
                value: '3',
                label: readMessage(
                  messages,
                  'writeReview.rating_3',
                  '3 - Good',
                ),
              },
              {
                value: '4',
                label: readMessage(
                  messages,
                  'writeReview.rating_4',
                  '4 - Very Good',
                ),
              },
              {
                value: '5',
                label: readMessage(
                  messages,
                  'writeReview.rating_5',
                  '5 - Excellent',
                ),
              },
            ]}
            error={ratingError}
          />

          <FormField
            as="textarea"
            label={readMessage(messages, 'writeReview.comment', 'Comment')}
            onChange={(event) => setFormComment(event.target.value)}
            placeholder={
              ownReviewState.isReviewed
                ? readMessage(
                    messages,
                    'writeReview.placeholder.edit',
                    'Update your thoughts on this product...',
                  )
                : readMessage(
                    messages,
                    'writeReview.placeholder.new',
                    'Tell us what you liked or did not like about this product...',
                  )
            }
            required
            showRequiredAsterisk
            rows={5}
            value={formComment}
            error={commentError}
          />

          <p className="shop-panel__meta">
            {readMessage(
              messages,
              'writeReview.submit.helper',
              'Visible to other shoppers. You can edit later.',
            )}
          </p>

          {generalError ? (
            <div className="auth-message auth-message--error">
              {generalError}
            </div>
          ) : null}

          <FormActions className="product-review-form__actions">
            <Button
              type="submit"
              variant="transparent"
              className="app-button app-button--primary"
              disabled={Boolean(pendingAction) || isRefreshing}
            >
              {pendingAction === 'edit'
                ? readMessage(messages, 'writeReview.submit.editing', 'Saving…')
                : pendingAction === 'create'
                  ? readMessage(
                      messages,
                      'writeReview.submit.creating',
                      'Publishing…',
                    )
                  : ownReviewState.isReviewed
                    ? readMessage(
                        messages,
                        'writeReview.submit.edit',
                        'Save changes',
                      )
                    : readMessage(
                        messages,
                        'writeReview.submit.new',
                        'Publish review',
                      )}
            </Button>

            {ownReviewState.isReviewed ? (
              <Button
                variant="transparent"
                className="app-button app-button--danger"
                onClick={handleDeleteReview}
                disabled={pendingAction === 'delete' || isRefreshing}
              >
                {deleteActionLabel}
              </Button>
            ) : null}

            <Button
              variant="transparent"
              className="app-button app-button--ghost"
              onClick={closeReviewModal}
            >
              {messages['writeReview.cancelEdit'] || 'Cancel'}
            </Button>
          </FormActions>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
